import type { SiteBriefChoiceType, SiteBriefSurveyQuestionType, SiteBriefSurveyType } from '../api.types';

// A group of one selection question is a single card: it shows as a card and sends the chosen answer as it is.
export function getSiteBriefSurveyCard(survey: SiteBriefSurveyType): SiteBriefChoiceType | null {
  const [question] = survey.questions;
  if (survey.questions.length !== 1 || !question || question.kind === 'free_text') {
    return null;
  }
  return {
    kind: question.kind,
    question: question.question,
    multi_select: question.multi_select,
    ...(question.answer_prefix !== undefined ? { answer_prefix: question.answer_prefix } : {}),
    options: question.options,
    skip_answer: question.skip_answer,
    language: survey.language,
  };
}

export type SiteBriefChoiceDraft = { selected: number[]; customText: string; customSelected: boolean };
// "skipped" lists the questions the person skipped explicitly; they stay marked until answered.
export type SiteBriefSurveyDraft = { step: number; answers: Record<string, SiteBriefChoiceDraft>; skipped: string[] };
export const SITE_BRIEF_SURVEY_ANSWER_MAX_LENGTH = 2000;

const COPY = {
  en: {
    next: 'Next',
    submit: 'Send answers',
    skip: 'Skip question',
    question: 'Question',
    placeholder: 'Write your answer…',
    previousQuestion: 'Previous question',
    nextQuestion: 'Next question',
    allQuestions: 'All questions',
    current: 'Current question',
    upNext: 'Next:',
    counter: (step: number, total: number) => `${step} of ${total}`,
    showAll: (step: number, total: number) => `Question ${step} of ${total}, show all questions`,
    answered: (count: number, total: number) => `${count} of ${total} answered`,
    skippedCount: (count: number) => `${count} skipped`,
  },
  uk: {
    next: 'Далі',
    submit: 'Надіслати відповіді',
    skip: 'Пропустити питання',
    question: 'Питання',
    placeholder: 'Напишіть відповідь…',
    previousQuestion: 'Попереднє питання',
    nextQuestion: 'Наступне питання',
    allQuestions: 'Усі питання',
    current: 'Поточне питання',
    upNext: 'Далі:',
    counter: (step: number, total: number) => `${step} з ${total}`,
    showAll: (step: number, total: number) => `Питання ${step} з ${total}, показати всі питання`,
    answered: (count: number, total: number) => `Відповіді: ${count} з ${total}`,
    skippedCount: (count: number) => `Пропущено: ${count}`,
  },
  ru: {
    next: 'Далее',
    submit: 'Отправить ответы',
    skip: 'Пропустить вопрос',
    question: 'Вопрос',
    placeholder: 'Напишите ответ…',
    previousQuestion: 'Предыдущий вопрос',
    nextQuestion: 'Следующий вопрос',
    allQuestions: 'Все вопросы',
    current: 'Текущий вопрос',
    upNext: 'Далее:',
    counter: (step: number, total: number) => `${step} из ${total}`,
    showAll: (step: number, total: number) => `Вопрос ${step} из ${total}, показать все вопросы`,
    answered: (count: number, total: number) => `Ответы: ${count} из ${total}`,
    skippedCount: (count: number) => `Пропущено: ${count}`,
  },
  es: {
    next: 'Siguiente',
    submit: 'Enviar respuestas',
    skip: 'Omitir pregunta',
    question: 'Pregunta',
    placeholder: 'Escribe tu respuesta…',
    previousQuestion: 'Pregunta anterior',
    nextQuestion: 'Pregunta siguiente',
    allQuestions: 'Todas las preguntas',
    current: 'Pregunta actual',
    upNext: 'Siguiente:',
    counter: (step: number, total: number) => `${step} de ${total}`,
    showAll: (step: number, total: number) => `Pregunta ${step} de ${total}, mostrar todas las preguntas`,
    answered: (count: number, total: number) => `${count} de ${total} respondidas`,
    skippedCount: (count: number) => `Omitidas: ${count}`,
  },
};

export function getSiteBriefSurveyCopy(language = 'en') {
  const locale = language.toLowerCase().split(/[-_]/)[0];
  return COPY[(locale === 'ua' ? 'uk' : locale) as keyof typeof COPY] ?? COPY.en;
}

export function restoreSiteBriefSurveyDraft(survey: SiteBriefSurveyType, raw: string | null): SiteBriefSurveyDraft {
  const empty: SiteBriefSurveyDraft = { step: 0, answers: {}, skipped: [] };
  try {
    const saved = JSON.parse(raw ?? 'null');
    if (!saved || typeof saved !== 'object') {
      return empty;
    }
    const answers: SiteBriefSurveyDraft['answers'] = {};
    for (const question of survey.questions) {
      const draft = saved.answers?.[question.id];
      if (!draft || typeof draft !== 'object') {
        continue;
      }
      let selected = Array.isArray(draft.selected)
        ? [
            ...new Set<number>(
              draft.selected.filter(
                (index: unknown) =>
                  Number.isInteger(index) && Number(index) >= 0 && Number(index) < question.options.length,
              ),
            ),
          ]
        : [];
      const customText =
        typeof draft.customText === 'string' ? draft.customText.slice(0, SITE_BRIEF_SURVEY_ANSWER_MAX_LENGTH) : '';
      const customSelected =
        typeof draft.customSelected === 'boolean'
          ? draft.customSelected
          : !question.multi_select && Boolean(customText.trim());
      if (!question.multi_select) {
        selected = customSelected || question.kind === 'free_text' ? [] : selected.slice(0, 1);
      }
      answers[question.id] = { selected, customText, customSelected };
    }
    const step = Number.isInteger(saved.step) ? Math.max(0, Math.min(saved.step, survey.questions.length - 1)) : 0;
    // Drafts saved before skipped questions were marked have no list.
    const skipped = Array.isArray(saved.skipped)
      ? survey.questions
          .filter((question) => saved.skipped.includes(question.id) && !hasSiteBriefChoiceAnswer(question, answers[question.id]))
          .map((question) => question.id)
      : [];
    return { step, answers, skipped };
  } catch {
    return empty;
  }
}

// New multi-select cards carry list items from the backend. Old cards keep their complete sentences.
export function formatSiteBriefChoiceAnswer(
  question: Pick<SiteBriefSurveyQuestionType, 'kind' | 'multi_select' | 'answer_prefix' | 'options'>,
  draft: SiteBriefChoiceDraft | undefined,
): string {
  if (!draft) {
    return '';
  }
  const custom = (question.kind === 'free_text' || draft.customSelected) ? draft.customText.trim() : '';
  if (question.kind === 'free_text' || (!question.multi_select && custom)) {
    return custom;
  }
  const selected = [...new Set(draft.selected)]
    .filter((index) => Number.isInteger(index) && index >= 0 && index < question.options.length)
    .sort((left, right) => left - right);
  const answers = (question.multi_select ? selected : selected.slice(0, 1)).map((index) => question.options[index].answer);
  const presets = answers.length && question.multi_select && question.answer_prefix
    ? `${question.answer_prefix} ${answers.join(', ')}`
    : answers.join('\n');
  // Own text may be a complete sentence or a correction, so keep it verbatim on its own line.
  return [presets, custom].filter(Boolean).join('\n');
}

export function formatSiteBriefSurveyAnswers(survey: SiteBriefSurveyType, draft: SiteBriefSurveyDraft): string {
  const clean = restoreSiteBriefSurveyDraft(survey, JSON.stringify(draft));
  let answered = false;
  const blocks = survey.questions.map((question) => {
    const answer = formatSiteBriefChoiceAnswer(question, clean.answers[question.id]);
    if (answer) {
      answered = true;
    }
    return `${question.question}\n${answer || question.skip_answer}`;
  });
  return answered ? blocks.join('\n\n') : survey.skip_answer;
}

export function skipSiteBriefSurveyQuestion(
  survey: SiteBriefSurveyType,
  draft: SiteBriefSurveyDraft,
): SiteBriefSurveyDraft {
  const question = survey.questions[draft.step];
  return {
    step: Math.min(draft.step + 1, survey.questions.length - 1),
    answers: { ...draft.answers, [question.id]: { selected: [], customText: '', customSelected: false } },
    skipped: draft.skipped.includes(question.id) ? draft.skipped : [...draft.skipped, question.id],
  };
}

export function hasSiteBriefChoiceAnswer(
  question: Pick<SiteBriefSurveyQuestionType, 'kind'>,
  draft: SiteBriefChoiceDraft | undefined,
): boolean {
  if (!draft) {
    return false;
  }
  const customAnswer = draft.customText.trim().length > 0;
  if (question.kind === 'free_text') {
    return customAnswer;
  }
  return draft.selected.length > 0 || (draft.customSelected && customAnswer);
}

// A one-line summary of an answer for the list of questions: the chosen titles, then the person's own words.
export function describeSiteBriefChoiceAnswer(
  question: Pick<SiteBriefSurveyQuestionType, 'kind' | 'options'>,
  draft: SiteBriefChoiceDraft | undefined,
): string {
  if (!draft) {
    return '';
  }
  const parts = [...draft.selected]
    .sort((left, right) => left - right)
    .map((index) => question.options[index]?.title)
    .filter(Boolean);
  if ((question.kind === 'free_text' || draft.customSelected) && draft.customText.trim()) {
    parts.push(draft.customText.trim());
  }
  return parts.join(', ');
}
